import React, { useEffect, useState } from "react";
import { api } from "../lib/api";
import { Lock, ArrowRight, Loader2, LogOut, Plus, RefreshCw, Building2, Store, Megaphone, Inbox, MessageCircle, Mail, Users } from "lucide-react";
import { toast } from "sonner";

const TOKEN_KEY = "zelo_admin_token";

export default function AdminPage() {
  const [token, setToken] = useState(() => localStorage.getItem(TOKEN_KEY));
  const [tab, setTab] = useState("overview");

  if (!token) return <Login onLogin={(t) => { localStorage.setItem(TOKEN_KEY, t); setToken(t); }} />;

  const auth = { headers: { Authorization: `Bearer ${token}` } };

  const tabs = [
    { id: "overview", label: "Overview", Icon: RefreshCw },
    { id: "retail", label: "Add Retail Property", Icon: Store },
    { id: "preleased", label: "Add Pre-Leased", Icon: Building2 },
    { id: "demand", label: "Add Demand", Icon: Megaphone },
    { id: "broker-responses", label: "Broker Responses", Icon: Inbox },
    { id: "retail-inquiries", label: "Retail Inquiries", Icon: MessageCircle },
    { id: "listings", label: "Owner Submissions", Icon: Users },
    { id: "contacts", label: "Contact Messages", Icon: Mail },
  ];

  return (
    <div className="min-h-screen bg-neutral-50" data-testid="admin-page">
      <div className="bg-brand-ink text-white">
        <div className="container-zelo py-6 flex items-center justify-between">
          <div>
            <div className="font-russo text-2xl">Zelo · Admin</div>
            <div className="text-xs text-white/60 mt-1">Internal use only</div>
          </div>
          <button onClick={() => { localStorage.removeItem(TOKEN_KEY); setToken(null); }} className="inline-flex items-center gap-2 px-4 py-2 rounded-full border border-white/20 text-sm hover:bg-white/10" data-testid="admin-logout">
            <LogOut className="w-4 h-4" /> Logout
          </button>
        </div>
      </div>

      <div className="container-zelo py-8 grid grid-cols-1 lg:grid-cols-12 gap-8">
        <aside className="lg:col-span-3">
          <nav className="rounded-2xl border border-neutral-200 bg-white p-2 sticky top-6">
            {tabs.map((t) => (
              <button key={t.id} onClick={() => setTab(t.id)} data-testid={`admin-tab-${t.id}`}
                className={`w-full text-left flex items-center gap-2.5 px-3 py-2.5 rounded-lg text-sm font-medium transition ${tab === t.id ? "bg-brand text-white" : "hover:bg-neutral-50"}`}>
                <t.Icon className="w-4 h-4" /> {t.label}
              </button>
            ))}
          </nav>
        </aside>
        <main className="lg:col-span-9 space-y-6">
          {tab === "overview" && <Overview auth={auth} />}
          {tab === "retail" && <AddRetail auth={auth} />}
          {tab === "preleased" && <AddPreLeased auth={auth} />}
          {tab === "demand" && <AddDemand auth={auth} />}
          {tab === "broker-responses" && <ListView auth={auth} endpoint="/admin/demand-responses" title="Broker Responses" testId="admin-broker-responses" />}
          {tab === "retail-inquiries" && <ListView auth={auth} endpoint="/admin/retail-inquiries" title="Retail Inquiries" testId="admin-retail-inquiries" />}
          {tab === "listings" && <ListView auth={auth} endpoint="/admin/listings" title="Owner Listing Submissions" testId="admin-listings" />}
          {tab === "contacts" && <ListView auth={auth} endpoint="/admin/contacts" title="Contact Messages" testId="admin-contacts" />}
        </main>
      </div>
    </div>
  );
}

function Login({ onLogin }) {
  const [pwd, setPwd] = useState("");
  const [loading, setLoading] = useState(false);
  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const r = await api.post("/admin/login", { password: pwd });
      onLogin(r.data.token);
      toast.success("Welcome to Zelo Admin");
    } catch (err) { toast.error(err?.response?.data?.detail || "Invalid password"); }
    finally { setLoading(false); }
  };
  return (
    <div className="min-h-screen bg-brand-ink text-white flex items-center justify-center p-6" data-testid="admin-login">
      <form onSubmit={submit} className="w-full max-w-sm bg-white text-neutral-900 rounded-2xl p-8 shadow-2xl">
        <div className="font-russo text-3xl text-brand">Zelo · Admin</div>
        <div className="text-sm text-neutral-500 mt-2">Sign in with the admin token (set on backend as <code className="text-brand">ADMIN_TOKEN</code>).</div>
        <div className="mt-6">
          <div className="rounded-xl border border-neutral-200 focus-within:border-brand focus-within:ring-2 focus-within:ring-brand/20 transition flex items-center px-4 py-3">
            <Lock className="w-4 h-4 text-neutral-400 mr-2" />
            <input type="password" placeholder="Admin password" value={pwd} onChange={(e) => setPwd(e.target.value)} className="flex-1 outline-none text-sm" data-testid="admin-password-input" />
          </div>
        </div>
        <button disabled={loading} className="mt-5 w-full rounded-full bg-brand text-white font-semibold py-3 hover:bg-brand-600 transition flex items-center justify-center gap-2 disabled:opacity-60" data-testid="admin-login-btn">
          {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <>Continue <ArrowRight className="w-4 h-4" /></>}
        </button>
      </form>
    </div>
  );
}

function Overview({ auth }) {
  const [data, setData] = useState(null);
  const refetch = () => api.get("/admin/overview", auth).then((r) => setData(r.data)).catch(() => toast.error("Failed to load"));
  useEffect(() => { refetch(); /* eslint-disable-next-line */ }, []);
  if (!data) return <Card><div className="text-neutral-500">Loading…</div></Card>;
  const cells = [
    ["Pre-Leased", data.preleased_count],
    ["Retail", data.retail_count],
    ["Demands", data.demands_count],
    ["Broker Responses", data.demand_responses_count],
    ["Retail Inquiries", data.retail_inquiries_count],
    ["Owner Submissions", data.listing_submissions_count],
    ["Contact Messages", data.contacts_count],
    ["Users", data.users_count],
  ];
  return (
    <Card>
      <div className="flex items-center justify-between mb-5">
        <div className="font-heading text-2xl font-medium">Overview</div>
        <button onClick={refetch} className="text-sm text-brand inline-flex items-center gap-1.5"><RefreshCw className="w-3.5 h-3.5" /> Refresh</button>
      </div>
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        {cells.map(([l, n]) => (
          <div key={l} className="rounded-xl border border-neutral-200 p-4">
            <div className="label-tiny">{l}</div>
            <div className="font-heading text-3xl font-medium text-brand mt-1">{n}</div>
          </div>
        ))}
      </div>
    </Card>
  );
}

function AddRetail({ auth }) {
  const [f, setF] = useState({
    state: "", city: "", area: "", building_name: "", lat: "", lng: "",
    floor: "", carpet_total: "", carpet_breakup: "", layout: "",
    rent_per_sqft: "", asking_total_rent: "", frontage_ft: "", height_ft: "",
    possession: "", nearby_tenants: "", facade_image: "", description: "", property_type: "High Street Retail",
  });
  const [loading, setLoading] = useState(false);
  const set = (k, v) => setF((p) => ({ ...p, [k]: v }));
  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const payload = {
        ...f,
        lat: parseFloat(f.lat), lng: parseFloat(f.lng),
        carpet_total: parseFloat(f.carpet_total), rent_per_sqft: parseFloat(f.rent_per_sqft),
        asking_total_rent: parseFloat(f.asking_total_rent || (f.rent_per_sqft * f.carpet_total)),
        frontage_ft: parseFloat(f.frontage_ft), height_ft: parseFloat(f.height_ft),
        nearby_tenants: f.nearby_tenants.split(",").map((s) => s.trim()).filter(Boolean),
        gallery: [f.facade_image].filter(Boolean),
      };
      await api.post("/admin/retail-properties", payload, auth);
      toast.success("Retail property added");
      setF({ ...f, state: "", city: "", area: "", building_name: "", lat: "", lng: "", floor: "", carpet_total: "", carpet_breakup: "", layout: "", rent_per_sqft: "", asking_total_rent: "", frontage_ft: "", height_ft: "", possession: "", nearby_tenants: "", facade_image: "", description: "" });
    } catch (err) { toast.error(err?.response?.data?.detail || "Failed"); }
    finally { setLoading(false); }
  };
  return (
    <Card>
      <Title>Add Retail Property</Title>
      <form onSubmit={submit} className="grid grid-cols-1 md:grid-cols-2 gap-3" data-testid="admin-retail-form">
        <In l="State *" v={f.state} onChange={(v) => set("state", v)} testId="ar-state" />
        <In l="City *" v={f.city} onChange={(v) => set("city", v)} testId="ar-city" />
        <In l="Area *" v={f.area} onChange={(v) => set("area", v)} testId="ar-area" />
        <In l="Building Name (internal)" v={f.building_name} onChange={(v) => set("building_name", v)} testId="ar-building" />
        <In l="Latitude *" v={f.lat} onChange={(v) => set("lat", v)} testId="ar-lat" />
        <In l="Longitude *" v={f.lng} onChange={(v) => set("lng", v)} testId="ar-lng" />
        <In l="Floor (e.g., Ground + First) *" v={f.floor} onChange={(v) => set("floor", v)} testId="ar-floor" />
        <In l="Carpet Total (sqft) *" v={f.carpet_total} onChange={(v) => set("carpet_total", v)} testId="ar-carpet" />
        <In l="Carpet Breakup (e.g., G:1500 · 1st:900)" v={f.carpet_breakup} onChange={(v) => set("carpet_breakup", v)} testId="ar-breakup" full />
        <In l="Layout" v={f.layout} onChange={(v) => set("layout", v)} testId="ar-layout" full />
        <In l="Rent ₹/sqft *" v={f.rent_per_sqft} onChange={(v) => set("rent_per_sqft", v)} testId="ar-rent" />
        <In l="Asking Total ₹/month (optional)" v={f.asking_total_rent} onChange={(v) => set("asking_total_rent", v)} testId="ar-asking" />
        <In l="Frontage (ft) *" v={f.frontage_ft} onChange={(v) => set("frontage_ft", v)} testId="ar-frontage" />
        <In l="Height (ft) *" v={f.height_ft} onChange={(v) => set("height_ft", v)} testId="ar-height" />
        <In l="Possession (e.g., Aug 2026) *" v={f.possession} onChange={(v) => set("possession", v)} testId="ar-possession" />
        <In l="Property Type" v={f.property_type} onChange={(v) => set("property_type", v)} testId="ar-type" />
        <In l="Nearby Tenants (comma separated)" v={f.nearby_tenants} onChange={(v) => set("nearby_tenants", v)} testId="ar-nearby" full />
        <In l="Facade Image URL" v={f.facade_image} onChange={(v) => set("facade_image", v)} testId="ar-facade" full />
        <In l="Description" v={f.description} onChange={(v) => set("description", v)} testId="ar-desc" full textarea />
        <div className="md:col-span-2">
          <button disabled={loading} className="rounded-full bg-brand text-white px-7 py-3 text-sm font-semibold hover:bg-brand-600 transition shadow-brand inline-flex items-center gap-2 disabled:opacity-60" data-testid="ar-submit">
            {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <><Plus className="w-4 h-4" /> Add Retail Property</>}
          </button>
        </div>
      </form>
    </Card>
  );
}

function AddPreLeased({ auth }) {
  const [f, setF] = useState({
    title: "", tenant_name: "", tenant_industry: "", tenant_logo: "",
    city: "", area: "", building_name: "", full_address: "",
    property_type: "Showroom", floor: "Ground",
    super_built_up_area: "", carpet_area: "",
    asking_price: "", monthly_rent: "", roi: "",
    lease_period_years: 9, lockin_years: 5, lease_start: "",
    rent_escalation_percent: 15, escalation_every_years: 3,
    security_deposit: "", property_tax_paid_by: "Landlord", maintenance_paid_by: "Tenant",
    image: "", description: "", is_deal_of_month: false, is_top_pick: true,
  });
  const [loading, setLoading] = useState(false);
  const set = (k, v) => setF((p) => ({ ...p, [k]: v }));
  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const num = (k) => f[k] ? parseFloat(f[k]) : 0;
      const payload = { ...f,
        super_built_up_area: num("super_built_up_area"), carpet_area: num("carpet_area"),
        asking_price: num("asking_price"), monthly_rent: num("monthly_rent"), roi: num("roi"),
        lease_period_years: parseInt(f.lease_period_years), lockin_years: parseInt(f.lockin_years),
        rent_escalation_percent: parseFloat(f.rent_escalation_percent), escalation_every_years: parseInt(f.escalation_every_years),
        security_deposit: num("security_deposit"), gallery: [f.image].filter(Boolean),
      };
      await api.post("/admin/properties", payload, auth);
      toast.success("Pre-leased property added");
    } catch (err) { toast.error(err?.response?.data?.detail || "Failed"); }
    finally { setLoading(false); }
  };
  return (
    <Card>
      <Title>Add Pre-Leased Property</Title>
      <p className="text-sm text-neutral-500 mb-4">Brand name (tenant_name) is stored for internal records only — public site shows category only.</p>
      <form onSubmit={submit} className="grid grid-cols-1 md:grid-cols-2 gap-3" data-testid="admin-preleased-form">
        <In l="Title (internal)" v={f.title} onChange={(v) => set("title", v)} testId="ap-title" full />
        <In l="Tenant Name (internal)" v={f.tenant_name} onChange={(v) => set("tenant_name", v)} testId="ap-tenant" />
        <In l="Tenant Industry / Category *" v={f.tenant_industry} onChange={(v) => set("tenant_industry", v)} testId="ap-industry" />
        <In l="City *" v={f.city} onChange={(v) => set("city", v)} testId="ap-city" />
        <In l="Area *" v={f.area} onChange={(v) => set("area", v)} testId="ap-area" />
        <In l="Building Name (internal)" v={f.building_name} onChange={(v) => set("building_name", v)} testId="ap-building" />
        <In l="Full Address (internal)" v={f.full_address} onChange={(v) => set("full_address", v)} testId="ap-address" full />
        <In l="Property Type *" v={f.property_type} onChange={(v) => set("property_type", v)} testId="ap-type" />
        <In l="Floor *" v={f.floor} onChange={(v) => set("floor", v)} testId="ap-floor" />
        <In l="Super Built Up (sqft) *" v={f.super_built_up_area} onChange={(v) => set("super_built_up_area", v)} testId="ap-sba" />
        <In l="Carpet Area (sqft) *" v={f.carpet_area} onChange={(v) => set("carpet_area", v)} testId="ap-carpet" />
        <In l="Asking Price (₹) *" v={f.asking_price} onChange={(v) => set("asking_price", v)} testId="ap-price" />
        <In l="Monthly Rent (₹) *" v={f.monthly_rent} onChange={(v) => set("monthly_rent", v)} testId="ap-rent" />
        <In l="ROI %" v={f.roi} onChange={(v) => set("roi", v)} testId="ap-roi" />
        <In l="Lease Years" v={f.lease_period_years} onChange={(v) => set("lease_period_years", v)} testId="ap-lease" />
        <In l="Lock-in Years" v={f.lockin_years} onChange={(v) => set("lockin_years", v)} testId="ap-lockin" />
        <In l="Lease Start (YYYY-MM-DD)" v={f.lease_start} onChange={(v) => set("lease_start", v)} testId="ap-leasestart" />
        <In l="Escalation %" v={f.rent_escalation_percent} onChange={(v) => set("rent_escalation_percent", v)} testId="ap-escalation" />
        <In l="Escalate Every (yrs)" v={f.escalation_every_years} onChange={(v) => set("escalation_every_years", v)} testId="ap-escevery" />
        <In l="Security Deposit (₹)" v={f.security_deposit} onChange={(v) => set("security_deposit", v)} testId="ap-deposit" />
        <In l="Image URL" v={f.image} onChange={(v) => set("image", v)} testId="ap-image" full />
        <In l="Description" v={f.description} onChange={(v) => set("description", v)} testId="ap-desc" full textarea />
        <div className="md:col-span-2 flex gap-4 text-sm">
          <label className="inline-flex items-center gap-2"><input type="checkbox" className="accent-[#5D33F6]" checked={f.is_deal_of_month} onChange={(e) => set("is_deal_of_month", e.target.checked)} /> Deal of the Month</label>
          <label className="inline-flex items-center gap-2"><input type="checkbox" className="accent-[#5D33F6]" checked={f.is_top_pick} onChange={(e) => set("is_top_pick", e.target.checked)} /> Top Pick</label>
        </div>
        <div className="md:col-span-2">
          <button disabled={loading} className="rounded-full bg-brand text-white px-7 py-3 text-sm font-semibold hover:bg-brand-600 transition shadow-brand inline-flex items-center gap-2 disabled:opacity-60" data-testid="ap-submit">
            {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <><Plus className="w-4 h-4" /> Add Pre-Leased Property</>}
          </button>
        </div>
      </form>
    </Card>
  );
}

function AddDemand({ auth }) {
  const [f, setF] = useState({
    demand_code: "", category: "", city: "", state: "", preferred_areas: "",
    carpet_min: "", carpet_max: "", floor_pref: "", frontage_min_ft: "", height_min_ft: "",
    possession_by: "", rent_budget_max_per_sqft: "", notes: "", is_active: true,
  });
  const [loading, setLoading] = useState(false);
  const set = (k, v) => setF((p) => ({ ...p, [k]: v }));
  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const num = (k) => f[k] ? parseFloat(f[k]) : null;
      const payload = { ...f,
        preferred_areas: f.preferred_areas.split(",").map((s) => s.trim()).filter(Boolean),
        carpet_min: num("carpet_min"), carpet_max: num("carpet_max"),
        frontage_min_ft: num("frontage_min_ft"), height_min_ft: num("height_min_ft"),
        rent_budget_max_per_sqft: num("rent_budget_max_per_sqft"),
      };
      await api.post("/admin/demands", payload, auth);
      toast.success("Demand added — visible on /broker now");
    } catch (err) { toast.error(err?.response?.data?.detail || "Failed"); }
    finally { setLoading(false); }
  };
  return (
    <Card>
      <Title>Add Demand (Broker Page)</Title>
      <form onSubmit={submit} className="grid grid-cols-1 md:grid-cols-2 gap-3" data-testid="admin-demand-form">
        <In l="Demand Code (e.g., ZD-2026-009) *" v={f.demand_code} onChange={(v) => set("demand_code", v)} testId="ad-code" />
        <In l="Category (e.g., Showroom, QSR) *" v={f.category} onChange={(v) => set("category", v)} testId="ad-cat" />
        <In l="City *" v={f.city} onChange={(v) => set("city", v)} testId="ad-city" />
        <In l="State *" v={f.state} onChange={(v) => set("state", v)} testId="ad-state" />
        <In l="Preferred Areas (comma separated)" v={f.preferred_areas} onChange={(v) => set("preferred_areas", v)} testId="ad-areas" full />
        <In l="Carpet Min (sqft)" v={f.carpet_min} onChange={(v) => set("carpet_min", v)} testId="ad-cmin" />
        <In l="Carpet Max (sqft)" v={f.carpet_max} onChange={(v) => set("carpet_max", v)} testId="ad-cmax" />
        <In l="Floor Preference" v={f.floor_pref} onChange={(v) => set("floor_pref", v)} testId="ad-floor" />
        <In l="Frontage Min (ft)" v={f.frontage_min_ft} onChange={(v) => set("frontage_min_ft", v)} testId="ad-frontage" />
        <In l="Height Min (ft)" v={f.height_min_ft} onChange={(v) => set("height_min_ft", v)} testId="ad-height" />
        <In l="Possession By (e.g., Q3 2026)" v={f.possession_by} onChange={(v) => set("possession_by", v)} testId="ad-possession" />
        <In l="Rent Budget Max ₹/sqft" v={f.rent_budget_max_per_sqft} onChange={(v) => set("rent_budget_max_per_sqft", v)} testId="ad-budget" />
        <In l="Notes (visible to brokers)" v={f.notes} onChange={(v) => set("notes", v)} testId="ad-notes" full textarea />
        <div className="md:col-span-2">
          <button disabled={loading} className="rounded-full bg-brand text-white px-7 py-3 text-sm font-semibold hover:bg-brand-600 transition shadow-brand inline-flex items-center gap-2 disabled:opacity-60" data-testid="ad-submit">
            {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <><Plus className="w-4 h-4" /> Publish Demand</>}
          </button>
        </div>
      </form>
    </Card>
  );
}

function ListView({ auth, endpoint, title, testId }) {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const refetch = () => { setLoading(true); api.get(endpoint, auth).then((r) => setItems(r.data.items || [])).finally(() => setLoading(false)); };
  useEffect(() => { refetch(); /* eslint-disable-next-line */ }, [endpoint]);
  return (
    <Card>
      <div className="flex items-center justify-between mb-4">
        <Title>{title}</Title>
        <button onClick={refetch} className="text-sm text-brand inline-flex items-center gap-1.5"><RefreshCw className="w-3.5 h-3.5" /> Refresh</button>
      </div>
      <div className="space-y-3" data-testid={testId}>
        {loading && <div className="text-neutral-500">Loading…</div>}
        {!loading && items.length === 0 && <div className="text-neutral-500">No records yet.</div>}
        {items.map((it, i) => (
          <details key={it.id || i} className="rounded-xl border border-neutral-200 p-4 bg-neutral-50/40">
            <summary className="cursor-pointer text-sm font-medium flex items-center justify-between">
              <span>{it.demand_code || it.name || it.broker_name || it.first_name || it.email || it.id || `#${i + 1}`} <span className="text-neutral-400 ml-2">{it.created_at?.slice(0, 16).replace("T", " ")}</span></span>
            </summary>
            <pre className="mt-3 text-xs whitespace-pre-wrap break-words text-neutral-700">{JSON.stringify(it, null, 2)}</pre>
          </details>
        ))}
      </div>
    </Card>
  );
}

function Card({ children }) { return <div className="rounded-2xl border border-neutral-200 bg-white p-6 md:p-8">{children}</div>; }
function Title({ children }) { return <div className="font-heading text-2xl font-medium tracking-tight">{children}</div>; }
function In({ l, v, onChange, full, textarea, testId }) {
  return (
    <div className={full ? "md:col-span-2" : ""}>
      <div className="text-xs font-semibold text-neutral-600 mb-1.5">{l}</div>
      {textarea
        ? <textarea rows={3} className={inp} value={v} onChange={(e) => onChange(e.target.value)} data-testid={testId} />
        : <input className={inp} value={v} onChange={(e) => onChange(e.target.value)} data-testid={testId} />}
    </div>
  );
}
const inp = "w-full px-3 py-2.5 rounded-xl border border-neutral-200 outline-none focus:border-brand focus:ring-2 focus:ring-brand/20 text-sm bg-white";
